import { createContext, useContext, useEffect, useReducer, useMemo, useCallback } from 'react';
import { api } from '../api.js';
import { useAuth } from './AuthContext.jsx';

const Ctx = createContext(null);

const initial = {
  loading: false,
  error: null,
  periods: [],
  episodes: [],
  meds: [],
  todayMeds: { date: null, items: [] },
  phase: { phase: 'unknown', dayOfCycle: null },
  cycleStats: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'set_loading':
      return { ...state, loading: action.value };
    case 'set_error':
      return { ...state, error: action.error, loading: false };
    case 'set_all':
      return { ...state, ...action.patch, loading: false, error: null };
    default:
      return state;
  }
}

export function AppDataProvider({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initial);

  const refresh = useCallback(async () => {
    if (!user) return;
    dispatch({ type: 'set_loading', value: true });
    try {
      const [periodsRes, episodesRes, medsRes, dayRes, phaseRes, statsRes] = await Promise.all([
        api.get('/cycle'),
        api.get('/headache'),
        api.get('/medication/meds'),
        api.get('/medication/day'),
        api.get('/cycle/phase'),
        api.get('/cycle/stats').catch(() => null),
      ]);
      dispatch({
        type: 'set_all',
        patch: {
          periods: periodsRes.periods || [],
          episodes: episodesRes.episodes || [],
          meds: medsRes.meds || [],
          todayMeds: dayRes,
          phase: phaseRes,
          cycleStats: statsRes,
        },
      });
    } catch (err) {
      dispatch({ type: 'set_error', error: err.message });
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  // --- azioni ---
  const addPeriod = useCallback(async (body) => {
    await api.post('/cycle', body);
    await refresh();
  }, [refresh]);
  const updatePeriod = useCallback(async (id, body) => {
    await api.patch(`/cycle/${id}`, body);
    await refresh();
  }, [refresh]);
  const deletePeriod = useCallback(async (id) => {
    await api.del(`/cycle/${id}`);
    await refresh();
  }, [refresh]);

  const addEpisode = useCallback(async (body) => {
    await api.post('/headache', body);
    await refresh();
  }, [refresh]);
  const updateEpisode = useCallback(async (id, body) => {
    await api.patch(`/headache/${id}`, body);
    await refresh();
  }, [refresh]);
  const deleteEpisode = useCallback(async (id) => {
    await api.del(`/headache/${id}`);
    await refresh();
  }, [refresh]);

  const addMed = useCallback(async (body) => {
    await api.post('/medication/meds', body);
    await refresh();
  }, [refresh]);
  const updateMed = useCallback(async (id, body) => {
    await api.patch(`/medication/meds/${id}`, body);
    await refresh();
  }, [refresh]);
  const deleteMed = useCallback(async (id) => {
    await api.del(`/medication/meds/${id}`);
    await refresh();
  }, [refresh]);

  const addIntake = useCallback(async (medId, takenAt = null, note = '') => {
    const body = { medId };
    if (takenAt) body.takenAt = takenAt;
    if (note) body.note = note;
    await api.post('/medication/intakes', body);
    await refresh();
  }, [refresh]);

  const updateIntake = useCallback(async (intakeId, body) => {
    await api.patch(`/medication/intakes/${intakeId}`, body);
    await refresh();
  }, [refresh]);

  const removeIntake = useCallback(async (intakeId) => {
    await api.del(`/medication/intakes/${intakeId}`);
    await refresh();
  }, [refresh]);

  const seedDefaultMeds = useCallback(async () => {
    await api.post('/medication/meds/seed-defaults');
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      addPeriod,
      updatePeriod,
      deletePeriod,
      addEpisode,
      updateEpisode,
      deleteEpisode,
      addMed,
      updateMed,
      deleteMed,
      addIntake,
      updateIntake,
      removeIntake,
      seedDefaultMeds,
    }),
    [
      state,
      refresh,
      addPeriod,
      updatePeriod,
      deletePeriod,
      addEpisode,
      updateEpisode,
      deleteEpisode,
      addMed,
      updateMed,
      deleteMed,
      addIntake,
      updateIntake,
      removeIntake,
      seedDefaultMeds,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppData() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAppData deve essere usato dentro AppDataProvider');
  return ctx;
}
